import React, { useMemo, useState } from 'react';
import {
  IconButton,
  InputAdornment,
  InputProps as MUIInputProps,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Delete2Light } from '@ha/icons';
import { useIntl } from 'ha/i18n';
import { MIN_SEARCH_INPUT_LENGTH, Locations } from 'ha/modules/Locations';
import { units } from '@hbf/dsl';

import { SimpleItem } from 'ha/modules/Locations/LocationAutocompleteInput';
import { CityTextField } from './CityTextField';

const useStyles = makeStyles()(() => ({
  city: {
    width: '250px',
    '& > div > div ': {
      margin: units.yx(0, 0.2),
    },
  },
  invalidCity: {
    '& > div': {
      border: '1px solid red',
    },
  },
  deleteButton: {
    position: 'absolute',
    right: 0,
    '& svg': {
      fontSize: units.rem(1),
    },
  },
  validation: {
    fontSize: units.rem(0.75),
    color: 'red',
    paddingLeft: units.rem(0.5),
    lineHeight: units.rem(3),
  },
}));

export interface Props {
  value: string;
  onChange: (value: string) => void;
  initialItems: SimpleItem[];
  changeOnlyOnSelect?: boolean;
  hasDeleteButton?: boolean;
}

const PredictionTypes: string[] = ['(cities)'];

const PlaceFilter: React.FC<Props> = ({
  value,
  onChange,
  initialItems,
  changeOnlyOnSelect,
  hasDeleteButton = false,
}) => {
  const { T } = useIntl();
  const { classes, cx } = useStyles();
  const [inputValue, setInputValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  React.useEffect(() => {
    setInputValue(value);
  }, [value]);

  const [renderInitialItems, setRenderInitialItems] = useState(false);

  React.useEffect(() => {
    const length = value?.length ?? 0;

    // User erased all symbols, lets show initial suggestions
    if (length === 0) {
      setRenderInitialItems(true);
    }

    // User typed MIN_SEARCH_INPUT_LENGTH letters
    // lets hide initial suggestions and show ones from google
    if (length >= MIN_SEARCH_INPUT_LENGTH && renderInitialItems) {
      setRenderInitialItems(false);
    }
  }, [renderInitialItems, value]);

  const handleSelect = React.useCallback(
    (newValue: string) => {
      if (!newValue) {
        setIsValid(false);
        return;
      }
      setIsValid(true);
      onChange(newValue);
    },
    [onChange],
  );

  const handleChange = React.useCallback(
    (newValue: string) => {
      if (changeOnlyOnSelect) {
        setInputValue(newValue);
      } else {
        onChange(newValue);
      }
    },
    [changeOnlyOnSelect, onChange],
  );

  const deleteButton = useMemo(
    () =>
      hasDeleteButton ? (
        <InputAdornment position="end" className={classes.deleteButton}>
          <IconButton onClick={() => setInputValue('')} size="small">
            <Delete2Light />
          </IconButton>
        </InputAdornment>
      ) : null,
    [hasDeleteButton],
  );

  return (
    <Locations
      renderInput={({ inputProps, InputProps, InputLabelProps, ...other }) => {
        const {
          ref,
          onChange: onChangeInput,
          onFocus,
          onBlur,
        } = inputProps as MUIInputProps;

        return (
          <React.Fragment>
            <CityTextField
              size="small"
              margin="dense"
              fullWidth
              className={cx({
                [classes.city]: hasDeleteButton,
                [classes.invalidCity]: !isValid,
              })}
              variant="outlined"
              value={inputValue}
              InputProps={{
                ref: InputProps.ref as React.RefObject<HTMLInputElement>,
                inputRef: ref as React.RefObject<HTMLInputElement>,
                endAdornment: deleteButton,
                ...other,
              }}
              onChange={onChangeInput}
              onFocus={onFocus}
              onBlur={onBlur}
              placeholder={T('Where will you go?')}
            />
            {hasDeleteButton && !isValid && (
              <span className={classes.validation}>
                {T('home_search_bar_error_place_is_required')}
              </span>
            )}
          </React.Fragment>
        );
      }}
      value={inputValue}
      predictionTypes={PredictionTypes}
      onSelect={handleSelect}
      onChange={handleChange}
      renderInitialItems={renderInitialItems}
      initialItems={initialItems}
    />
  );
};

export { PlaceFilter };
